import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthProvider";
import { api, resolveMediaUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, UserCircle2, Loader2, Check, Camera, Trash2, ImagePlus } from "lucide-react";
import { toast } from "sonner";

function Field({ label, missing, hint, children }) {
  return (
    <div className={`space-y-1.5 ${missing ? "p-3 -m-3 border border-rose-500/40 bg-rose-50/40 dark:bg-rose-950/15" : ""}`}>
      <label className="eyebrow flex items-center gap-2">
        {label}
        {missing && <span className="text-[9px] tracking-[0.18em] text-rose-600 dark:text-rose-400">· MISSING</span>}
      </label>
      {children}
      {missing && hint && <p className="text-[10px] text-rose-600/80 dark:text-rose-400/70">{hint}</p>}
    </div>
  );
}

export default function PersonalDetailsPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", username: "", bio: "", privacy: "public",
    age: "", gender: "", height_cm: "", weight_kg: "",
    city: "", country: "", occupation: "", body_type: "",
  });
  const [missing, setMissing] = useState(new Set());
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "", username: user.username || "", bio: user.bio || "",
      privacy: user.privacy || "public",
      age: user.age || "", gender: user.gender || "",
      height_cm: user.height_cm || "", weight_kg: user.weight_kg || "",
      city: user.city || "", country: user.country || "", occupation: user.occupation || "",
      body_type: user.body_type || "",
    });
    api.get("/users/me/completion").then(({ data }) => setMissing(new Set(data.missing || [])));
  }, [user]);

  const checkUsername = async (u) => {
    if (!u || u === user?.username) { setUsernameStatus(null); return; }
    try {
      const { data } = await api.get(`/users/check-username?u=${encodeURIComponent(u)}`);
      setUsernameStatus(data);
    } catch { setUsernameStatus(null); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : null,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
      };
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      await api.patch("/users/me", payload);
      await refresh();
      toast.success("Personal details saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <button onClick={() => navigate("/profile")} className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-profile">
        <ChevronLeft size={14} /> Account
      </button>
      <p className="eyebrow mt-6 flex items-center gap-2"><UserCircle2 size={12} /> Stylebay · Personal</p>
      <h1 className="editorial-h1 mt-3">Personal details.</h1>

      {/* Identity photo — single primary shot used across the app as the user's avatar */}
      <section className="mt-10" data-testid="personal-identity-photo">
        <PrimaryIdentityPhoto onChange={refresh} />
      </section>

      {/* Profile detail (merged community profile fields) */}
      <section className="mt-6 border border-border p-6 lg:p-8" data-testid="profile-detail-section">
        <p className="eyebrow">Profile detail</p>
        <p className="text-xs text-muted-foreground mt-1">How you appear inside the Stylebay community.</p>
        <div className="grid md:grid-cols-2 gap-4 mt-5">
          <Field label="Display name">
            <Input data-testid="pd-name-input" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
          </Field>
          <Field label="Username">
            <Input data-testid="pd-username-input" value={form.username}
                   onChange={(e) => {
                     const v = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "");
                     setForm({ ...form, username: v }); checkUsername(v);
                   }} className="rounded-none" />
            {usernameStatus && (
              <p className={`text-[10px] mt-1 uppercase tracking-[0.18em] ${usernameStatus.available ? "text-accent" : "text-destructive"}`}>
                {usernameStatus.available ? "Available ✓" : usernameStatus.reason}
              </p>
            )}
          </Field>
          <div className="md:col-span-2">
            <Field label="Bio">
              <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                        placeholder="One line about your style…" className="rounded-none min-h-[60px]" />
            </Field>
          </div>
          <Field label="Privacy">
            <Select value={form.privacy} onValueChange={(v) => setForm({ ...form, privacy: v })}>
              <SelectTrigger className="rounded-none" data-testid="pd-privacy-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="followers_only">Followers only</SelectItem>
                <SelectItem value="private">Private</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </section>

      {/* Personal / physical / location */}
      <section className="mt-6 border border-border p-6 lg:p-8" data-testid="basics-section">
        <p className="eyebrow">Basics</p>
        <p className="text-xs text-muted-foreground mt-1">Used by AI Try-On, sizing &amp; weather-aware suggestions.</p>
        <div className="grid md:grid-cols-2 gap-4 mt-5">
          <Field label="Age" missing={missing.has("age")} hint="Helps personalize style age-range.">
            <Input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="rounded-none" />
          </Field>
          <Field label="Gender" missing={missing.has("gender")} hint="Used to filter Curated picks.">
            <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
              <SelectTrigger className="rounded-none"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="non-binary">Non-binary</SelectItem>
                <SelectItem value="prefer-not">Prefer not to say</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Height (cm)" missing={missing.has("height_cm")} hint="Improves Try-On proportion accuracy.">
            <Input type="number" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} className="rounded-none" />
          </Field>
          <Field label="Weight (kg)" missing={missing.has("weight_kg")} hint="Helps sizing & fit recommendations.">
            <Input type="number" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="rounded-none" />
          </Field>
          <Field label="City" missing={missing.has("city")} hint="Powers weather-aware outfits.">
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-none" />
          </Field>
          <Field label="Country" missing={missing.has("country")} hint="Localizes retailers & sizes.">
            <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-none" />
          </Field>
          <Field label="Occupation" missing={missing.has("occupation")} hint="Shapes workwear suggestions.">
            <Input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} className="rounded-none" />
          </Field>
        </div>
      </section>

      {/* Body Type — helps AI sizing & silhouette suggestions */}
      <section className="mt-6 border border-border p-6 lg:p-8" data-testid="body-type-section">
        <p className="eyebrow">Body type</p>
        <p className="text-xs text-muted-foreground mt-1">Pick the silhouette closest to yours. Powers AI outfit &amp; try-on accuracy.</p>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { id: "oval",             label: "Oval",              desc: "Fuller midsection, softer waist" },
            { id: "round",            label: "Round",             desc: "Balanced curves, defined bust" },
            { id: "rectangle",        label: "Rectangle",         desc: "Straight shoulders, hips & waist" },
            { id: "triangle",         label: "Triangle (Pear)",   desc: "Hips wider than shoulders" },
            { id: "inverted",         label: "Inverted Triangle", desc: "Broader shoulders, slimmer hips" },
            { id: "hourglass",        label: "Hourglass",         desc: "Bust & hips balanced, defined waist" },
          ].map((b) => {
            const active = form.body_type === b.id;
            return (
              <button
                key={b.id}
                type="button"
                data-testid={`body-type-${b.id}`}
                onClick={() => setForm({ ...form, body_type: b.id })}
                className={`text-left p-4 border transition-all ${
                  active
                    ? "bg-accent text-accent-foreground border-accent shadow-[0_4px_18px_-6px_rgba(0,102,102,0.35)]"
                    : "border-border hover:border-accent hover:bg-accent/5"
                }`}
              >
                <div className="mx-auto h-24 w-16 mb-3">
                  <BodyTypeIllustration shape={b.id} active={active} />
                </div>
                <p className="text-xs uppercase tracking-[0.18em] font-semibold text-center">{b.label}</p>
                <p className={`text-[11px] mt-1 leading-snug text-center ${active ? "opacity-80" : "text-muted-foreground"}`}>{b.desc}</p>
              </button>
            );
          })}
        </div>
      </section>

      <div className="sticky bottom-20 sm:bottom-6 mt-8 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="pd-save-btn"
          className="rounded-none uppercase tracking-[0.2em] text-[11px] h-11 px-8 shadow-lg"
        >
          {saving ? <><Loader2 size={14} className="animate-spin mr-2" />Saving</> : <><Check size={14} className="mr-2" />Save changes</>}
        </Button>
      </div>
    </div>
  );
}

/* Body-type silhouette illustration (inline SVG so no image assets needed). */
function BodyTypeIllustration({ shape, active }) {
  // Six clearly distinct torso silhouettes (front view). Each shape varies
  // the shoulder / waist / hip width so the user can eyeball the difference.
  // Coordinates run 0-64 wide, 0-90 tall. Origin at top-left.
  const S = {
    // [shoulder_half_w, waist_half_w, hip_half_w, waist_y_bias]
    rectangle: [17, 17, 17, 0],   // straight lines top to bottom
    hourglass: [22, 12, 22, 0],   // pinched waist, matched shoulders + hips
    triangle:  [15, 18, 24, 0],   // wider at hips (pear)
    inverted:  [24, 18, 15, 0],   // wider at shoulders
    oval:      [17, 23, 17, 4],   // fuller midsection, tapered top+bottom
    round:     [20, 23, 20, 0],   // rounded all-over
  }[shape] || [17, 17, 17, 0];
  const [sh, wa, hp, bias] = S;
  const cx = 32;
  const yShoulder = 8;
  const yWaist = 42 + bias;
  const yHip = 68;
  const yBottom = 82;

  // Cubic Bézier from shoulders → waist → hips using control points that
  // sit on either side of the waist line for a natural curve.
  const path = [
    `M ${cx - sh} ${yShoulder}`,
    `C ${cx - sh} ${yShoulder + 10}, ${cx - wa} ${yWaist - 10}, ${cx - wa} ${yWaist}`,
    `C ${cx - wa} ${yWaist + 10}, ${cx - hp} ${yHip - 6}, ${cx - hp} ${yHip}`,
    `L ${cx - hp + 2} ${yBottom}`,
    `L ${cx + hp - 2} ${yBottom}`,
    `L ${cx + hp} ${yHip}`,
    `C ${cx + hp} ${yHip - 6}, ${cx + wa} ${yWaist + 10}, ${cx + wa} ${yWaist}`,
    `C ${cx + wa} ${yWaist - 10}, ${cx + sh} ${yShoulder + 10}, ${cx + sh} ${yShoulder}`,
    "Z",
  ].join(" ");

  const stroke = active ? "currentColor" : "hsl(var(--foreground) / 0.55)";
  const fill = active ? "currentColor" : "hsl(var(--foreground) / 0.08)";

  return (
    <svg
      viewBox="0 0 64 90"
      xmlns="http://www.w3.org/2000/svg"
      className="h-full w-full"
      aria-hidden="true"
    >
      {/* small head + neck for silhouette context */}
      <circle cx={cx} cy={-2} r={4.5} stroke={stroke} strokeWidth="1.4" fill="none" />
      <path
        d={`M ${cx - 3} 3 L ${cx - 3} ${yShoulder - 1} L ${cx + 3} ${yShoulder - 1} L ${cx + 3} 3`}
        stroke={stroke}
        strokeWidth="1.4"
        fill="none"
      />
      {/* torso silhouette */}
      <path
        d={path}
        stroke={stroke}
        strokeWidth="1.6"
        fill={fill}
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Compact primary-identity-photo manager for Personal Details.
 * - Shows the current primary photo (used as the user's avatar / try-on default)
 * - Replace (upload a new one and mark it primary)
 * - Remove (delete the primary; another photo auto-promotes on the backend)
 * Multi-photo gallery lives in AI Identity & Style page — kept out of Personal
 * Details per UX spec to simplify this screen.
 */
/**
 * Compact profile avatar manager for Personal Details.
 * - Updates ONLY the user's profile avatar (user.avatar_url).
 * - Does NOT touch identity_media — AI Try-On / Style DNA / portraits are
 *   managed separately from the AI Identity & Style page.
 */
function PrimaryIdentityPhoto({ onChange }) {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const avatarUrl = resolveMediaUrl(user?.avatar_url) || resolveMediaUrl(user?.picture);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile photo updated.");
      await refresh();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!avatarUrl) return;
    if (!window.confirm("Remove your profile photo?")) return;
    setBusy(true);
    try {
      await api.delete("/users/me/avatar");
      toast.success("Profile photo removed.");
      await refresh();
      onChange?.();
    } catch (e) {
      toast.error("Couldn't remove the photo. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border p-6 lg:p-8" data-testid="identity-photo-card">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <p className="eyebrow">Profile photo</p>
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          Used as your avatar only
        </p>
      </div>

      <div className="mt-5 flex items-start gap-5 flex-wrap">
        {/* Avatar preview */}
        <div className="h-28 w-28 border border-border bg-muted overflow-hidden flex items-center justify-center shrink-0 rounded-full">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="Profile"
              className="h-full w-full object-cover"
              data-testid="identity-photo-preview"
            />
          ) : (
            <UserCircle2 size={44} className="opacity-40" strokeWidth={1.2} />
          )}
        </div>

        {/* Controls */}
        <div className="flex-1 min-w-[180px] space-y-3">
          <p className="text-xs text-muted-foreground max-w-md">
            This is your display picture across Stylebay — profile, comments, community posts. It is <strong>not</strong> used for AI Try-On or Beauty analysis; those photos are managed separately on the AI Identity &amp; Style page.
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              hidden
              data-testid="identity-photo-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) upload(f);
              }}
            />
            <Button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="rounded-none uppercase tracking-[0.2em] text-[11px] h-10"
              data-testid="identity-photo-upload"
            >
              {busy ? (
                <><Loader2 size={13} className="animate-spin mr-2" />Uploading</>
              ) : avatarUrl ? (
                <><Camera size={13} className="mr-2" />Change photo</>
              ) : (
                <><ImagePlus size={13} className="mr-2" />Upload photo</>
              )}
            </Button>
            {avatarUrl && (
              <Button
                type="button"
                variant="outline"
                onClick={remove}
                disabled={busy}
                className="rounded-none uppercase tracking-[0.2em] text-[11px] h-10"
                data-testid="identity-photo-remove"
              >
                <Trash2 size={13} className="mr-2" />Remove
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

